
import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { FaUsersCog, FaPlus, FaEnvelope, FaLock, FaUserTag, FaIdCard, FaUpload, FaTrash, FaEdit } from 'react-icons/fa';
import { createNewMitraUser } from '../../supabase/auth';
import { createUserProfileDocument, getAllMitras, updateUserProfile, deleteUser } from '../../supabase/database';
import Papa from 'papaparse';
import Modal from '../../components/ui/Modal';

// Komponen Modal Edit dengan memoization
const EditMitraModal = memo(({ isOpen, onClose, mitra, onSave }) => {
  const [formData, setFormData] = useState({
    nama_mitra: '',
    kode_mitra: ''
  });

  useEffect(() => {
    if (mitra) {
      setFormData({
        nama_mitra: mitra.nama_mitra || '',
        kode_mitra: mitra.kode_mitra || ''
      });
    }
  }, [mitra]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    onSave(mitra.id, formData);
  }, [mitra?.id, formData, onSave]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg m-4">
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Edit Data Mitra</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nama Mitra</label>
                <input name="nama_mitra" value={formData.nama_mitra} onChange={handleChange} className="mt-1 w-full p-2 border rounded-md"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Kode Mitra</label>
                <input name="kode_mitra" value={formData.kode_mitra} onChange={handleChange} className="mt-1 w-full p-2 border rounded-md"/>
              </div>
              {/* Persentase komisi per mitra dihilangkan */}
            </div>
          </div>
          <div className="bg-gray-50 px-6 py-3 flex justify-end gap-3 rounded-b-lg">
            <button type="button" onClick={onClose} className="bg-white hover:bg-gray-100 text-gray-700 font-bold py-2 px-4 border border-gray-300 rounded-lg">Batal</button>
            <button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-4 rounded-lg">Simpan Perubahan</button>
          </div>
        </form>
      </div>
    </div>
  );
});

const ManajemenMitra = () => {
  const [mitraList, setMitraList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    nama_mitra: '',
    kode_mitra: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  
  const [parsedData, setParsedData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMitra, setSelectedMitra] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Bulk selection states
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Memoized fetchMitra untuk mencegah re-creation
  const fetchMitra = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const mitra = await getAllMitras();
      setMitraList(mitra || []);
    } catch (err) {
      console.error("Gagal mengambil daftar mitra:", err);
      setError("Gagal memuat daftar mitra.");
      setMitraList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMitra();
  }, [fetchMitra]);

  // Memoized handlers untuk performance
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleAddMitra = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    if (formData.password.length < 6) {
      setError("Password minimal harus 6 karakter.");
      setIsSubmitting(false);
      return;
    }

    try {
      const { user } = await createNewMitraUser(formData.email, formData.password);
      await createUserProfileDocument(user, { 
        role: 'mitra',
        nama_mitra: formData.nama_mitra,
        kode_mitra: formData.kode_mitra
      });
      setSuccess(`Berhasil menambahkan mitra baru: ${formData.nama_mitra}`);
      setFormData({ nama_mitra: '', kode_mitra: '', email: '', password: '' });
      fetchMitra();
    } catch (err) {
      setError("Gagal menambahkan mitra. Email mungkin sudah terdaftar.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, fetchMitra]);
  
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');
    setSuccess('');
    setParsedData([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: header => header.trim().toLowerCase(),
      complete: (results) => {
        const requiredHeaders = ['nama_mitra', 'kode_mitra', 'email', 'password'];
        const actualHeaders = results.meta.fields;
        const missingHeaders = requiredHeaders.filter(h => !actualHeaders.includes(h));

        if (missingHeaders.length > 0) {
          setError(`Header CSV tidak valid. Kolom yang hilang: ${missingHeaders.join(', ')}`);
          return;
        }
        setParsedData(results.data);
      }
    });
    e.target.value = null;
  }, []);

  const handleConfirmImport = useCallback(async () => {
    setIsImporting(true);
    setError('');
    setSuccess('');

    const validMitra = parsedData.filter(row => 
      row.nama_mitra && row.kode_mitra && row.email && row.password && row.password.length >= 6
    );

    if (validMitra.length === 0) {
      setError('Tidak ada data valid untuk diimpor.');
      setIsImporting(false);
      return;
    }

    let successCount = 0;
    let failCount = 0;
    let errorMessages = [];

    for (const mitra of validMitra) {
      try {
        const { user } = await createNewMitraUser(mitra.email, mitra.password);
        await createUserProfileDocument(user, { 
          role: 'mitra',
          nama_mitra: mitra.nama_mitra,
          kode_mitra: mitra.kode_mitra
        });
        successCount++;
      } catch (error) {
        failCount++;
        errorMessages.push(`Gagal impor ${mitra.email}: ${error.code}`);
        console.error(`Gagal impor ${mitra.email}:`, error);
      }
    }

    if(successCount > 0) setSuccess(`${successCount} mitra berhasil diimpor.`);
    if(failCount > 0) setError(`${failCount} mitra gagal diimpor. Alasan: ${errorMessages.join('; ')}`);
    
    setParsedData([]);
    setIsImporting(false);
    fetchMitra();
  }, [parsedData, fetchMitra]);

  const handleDeleteClick = useCallback((mitra) => {
    setSelectedMitra(mitra);
    setIsDeleteModalOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!selectedMitra) return;
    try {
      await deleteUser(selectedMitra.id);
      setSuccess(`Mitra ${selectedMitra.nama_mitra} berhasil dihapus.`);
      fetchMitra();
    } catch (err) {
      setError("Gagal menghapus mitra.");
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedMitra(null);
    }
  }, [selectedMitra, fetchMitra]);

  const handleEditClick = useCallback((mitra) => {
    setSelectedMitra(mitra);
    setIsEditModalOpen(true);
  }, []);

  const handleUpdateMitra = useCallback(async (mitraId, updatedData) => {
    try {
      await updateUserProfile(mitraId, {
        nama_mitra: updatedData.nama_mitra,
        kode_mitra: updatedData.kode_mitra
      });
      setSuccess('Data mitra berhasil diperbarui.');
      fetchMitra();
    } catch (error) {
      setError('Gagal memperbarui data mitra.');
      console.error(error);
    } finally {
      setIsEditModalOpen(false);
    }
  }, [fetchMitra]);

  // Bulk selection functions dengan useCallback
  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedItems(mitraList.map(mitra => mitra.id));
    } else {
      setSelectedItems([]);
    }
  }, [mitraList]);

  const handleSelectItem = useCallback((mitraId) => {
    setSelectedItems(prev => 
      prev.includes(mitraId) 
        ? prev.filter(id => id !== mitraId)
        : [...prev, mitraId]
    );
  }, []);

  const handleBulkAction = useCallback(() => {
    if (selectedItems.length === 0) {
      setError('Pilih minimal satu mitra untuk melakukan tindakan bulk.');
      return;
    }
    if (!bulkAction) {
      setError('Pilih jenis tindakan yang akan dilakukan.');
      return;
    }
    setShowBulkModal(true);
  }, [selectedItems.length, bulkAction]);

  const confirmBulkAction = useCallback(async () => {
    setShowBulkModal(false);
    setError('');
    setSuccess('');

    if (bulkAction === 'delete') {
      try {
        await Promise.all(selectedItems.map(id => deleteUser(id)));
        setSuccess(`${selectedItems.length} mitra berhasil dihapus.`);
        fetchMitra();
        setSelectedItems([]);
        setBulkAction('');
      } catch (err) {
        setError('Terjadi kesalahan saat menghapus mitra.');
        console.error(err);
      }
    }
  }, [bulkAction, selectedItems, fetchMitra]);

  return (
    <div className="p-4 md:p-8 bg-white min-h-full">
      {/* Bulk Action Confirmation Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Konfirmasi Tindakan Bulk</h2>
              <p className="text-gray-600 mb-6">
                Apakah Anda yakin ingin menerapkan tindakan ini pada <strong>{selectedItems.length}</strong> mitra yang dipilih?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
                >
                  Batal
                </button>
                <button
                  onClick={confirmBulkAction}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
                >
                  Ya, Terapkan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Hapus Mitra"
      >
        Apakah Anda yakin ingin menghapus mitra <strong>{selectedMitra?.nama_mitra}</strong>?
      </Modal>

      <EditMitraModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        mitra={selectedMitra}
        onSave={handleUpdateMitra}
      />

      <div className="flex items-center gap-4 mb-6">
        <FaUsersCog className="text-4xl text-amber-500" />
        <h1 className="text-3xl font-bold text-gray-800">Manajemen Mitra</h1>
      </div>

      {success && <p className="text-green-600 bg-green-50 p-3 rounded-md mb-4">{success}</p>}
      {error && <p className="text-red-600 bg-red-50 p-3 rounded-md mb-4">{error}</p>}

      <div className="bg-gray-50 p-6 rounded-lg shadow-sm mb-8">
        <h2 className="text-xl font-bold text-gray-700 mb-4">Tambah Mitra Baru</h2>
        <form onSubmit={handleAddMitra} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600">Nama Mitra</label>
              <div className="relative mt-1"><FaUserTag className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" /><input name="nama_mitra" value={formData.nama_mitra} onChange={handleChange} required className="w-full pl-10 p-2 border rounded-md"/></div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">Kode Mitra</label>
              <div className="relative mt-1"><FaIdCard className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" /><input name="kode_mitra" value={formData.kode_mitra} onChange={handleChange} required className="w-full pl-10 p-2 border rounded-md"/></div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">Komisi (%)</label>
              {/* Input persentase komisi dihapus */}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">Email Mitra</label>
              <div className="relative mt-1"><FaEnvelope className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" /><input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full pl-10 p-2 border rounded-md"/></div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">Password</label>
              <div className="relative mt-1"><FaLock className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" /><input type="password" name="password" value={formData.password} onChange={handleChange} required className="w-full pl-10 p-2 border rounded-md"/></div>
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={isSubmitting} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-4 rounded-lg">
              <FaPlus /> {isSubmitting ? 'Menambahkan...' : 'Tambah Mitra'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg shadow-sm mb-8">
        <h2 className="text-xl font-bold text-gray-700 mb-4">Impor Mitra dari CSV</h2>
        <input type="file" accept=".csv" onChange={handleFileSelect} className="hidden" ref={fileInputRef} />
        <button
          type="button"
          onClick={() => fileInputRef.current.click()}
          className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
        >
          <FaUpload /> Pilih File CSV
        </button>
        
        {parsedData.length > 0 && (
          <div className="mt-6">
            <h3 className="font-semibold">Pratinjau Data Impor ({parsedData.length} baris)</h3>
            <div className="overflow-x-auto max-h-60 mt-2 border rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-200"><tr>{Object.keys(parsedData[0]).map(key => <th key={key} className="p-2 text-left">{key}</th>)}</tr></thead>
                <tbody>{parsedData.slice(0, 5).map((row, i) => <tr key={i} className="border-t">{Object.values(row).map((val, j) => <td key={j} className="p-2">{val}</td>)}</tr>)}</tbody>
              </table>
            </div>
            <div className="mt-4 text-right">
              <button onClick={() => setParsedData([])} className="text-gray-600 font-bold py-2 px-4 rounded-lg mr-2">Batal</button>
              <button onClick={handleConfirmImport} disabled={isImporting} className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg">
                {isImporting ? 'Mengimpor...' : `Konfirmasi & Impor ${parsedData.length} Mitra`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {mitraList.length > 0 && (
        <div className="bg-gray-50 p-4 rounded-lg shadow-sm mb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                {selectedItems.length} dari {mitraList.length} dipilih
              </span>
            </div>
            
            {selectedItems.length > 0 && (
              <div className="flex items-center gap-3">
                <select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white"
                >
                  <option value="">Pilih Tindakan</option>
                  <option value="delete">Hapus Mitra</option>
                </select>
                
                <button
                  onClick={handleBulkAction}
                  disabled={!bulkAction}
                  className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-200"
                >
                  Terapkan
                </button>
                
                <button
                  onClick={() => {
                    setSelectedItems([]);
                    setBulkAction('');
                  }}
                  className="text-gray-500 hover:text-gray-700 text-sm font-medium"
                >
                  Batal Pilihan
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <h2 className="text-xl font-bold text-gray-700 p-6">Daftar Mitra Terdaftar</h2>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedItems.length === mitraList.length && mitraList.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kode</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama Mitra</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              {/* Kolom Komisi dihilangkan */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aksi</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {mitraList.map(mitra => (
              <tr 
                key={mitra.id}
                className={selectedItems.includes(mitra.id) ? 'bg-blue-50' : ''}
              >
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(mitra.id)}
                    onChange={() => handleSelectItem(mitra.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </td>
                <td className="px-6 py-4">{mitra.kode_mitra || 'N/A'}</td>
                <td className="px-6 py-4">{mitra.nama || mitra.kode_mitra || 'N/A'}</td>
                <td className="px-6 py-4">{mitra.email}</td>
                <td className="px-6 py-4 flex items-center gap-4">
                  <button onClick={() => handleEditClick(mitra)} className="text-blue-500 hover:text-blue-700">
                    <FaEdit />
                  </button>
                  <button onClick={() => handleDeleteClick(mitra)} className="text-red-500 hover:text-red-700">
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManajemenMitra;
